import { Obj } from "../internal";
import { FeatureStyle } from "../featuretree/internal";
import { FrameFeature } from "../framefeaturetree/internal";
/**
 * Handels add/delete of FrameFeatures that belong to this collection
 * @class FrameFeatureCollection
 * @constructor
 * @extends Obj
 */
export class FrameFeatureCollection extends Obj {
    constructor(scene, style, name, parent) {
        super();
        this.frameFeatures = [];
        this.defaultstyle = null;
        this.style = null;
        this.infrascene = scene.infrascene;
        this.scene = scene;
        this.name = name;
        this.parent = parent;
        this.defaultstyle = new FeatureStyle({
            stylename: "default",
            rules: [
                {
                    filter: "",
                    symbolizer: "polygon",
                    params: {
                        fill: [1, 0, 0, 0.2],
                        stroke: [1, 0, 0, 1.0],
                    },
                },
            ],
        });
        this.style = style || this.defaultstyle;
    }
    /**
     * @returns {FrameFeature} FrameFeature with this as parentcollection
     */
    createFrameFeature() {
        const feature = new FrameFeature(this);
        this.frameFeatures.push(feature);
        return feature;
    }
    /**
     * Create FrameFeatures from JSON Object with this as parentcollection from JSON Object
     * @param fc
     */
    parseFromJSON(fc) {
        if (fc["type"] == "FeatureCollection") {
            for (let i = 0; i < fc["features"].length; i++) {
                const f = fc["features"][i];
                const new_feature = this.createFrameFeature();
                new_feature.parseFromJSON(f, false);
            }
        }
    }
    /**
     * @returns {string} this as JSON valid string
     */
    toGeoJsonString() {
        let jsonstring = '{"type" : ' + '"FeatureCollection",';
        jsonstring += '"features" : [';
        for (let i = 0; i < this.frameFeatures.length; i++) {
            jsonstring += this.frameFeatures[i].toGeoJsonString();
            if (i < this.frameFeatures.length - 1)
                jsonstring += ",";
        }
        jsonstring += "]";
        jsonstring += this._getEPSGNodeString();
        jsonstring += "}";
        return jsonstring;
    }
    /**
     * Removes all FrameFeature from this
     */
    destroy() {
        for (let i = this.frameFeatures.length - 1; i > -1; i--) {
            this.destroyFrameFeature(this.frameFeatures[i]);
        }
    }
    /**
     * Removes specific FrameFeature
     * @param frameFeature will be removed from this
     */
    destroyFrameFeature(frameFeature) {
        for (let i = this.frameFeatures.length - 1; i >= 0; i--) {
            if (this.frameFeatures[i] == frameFeature) {
                this.frameFeatures[i].destroy();
                this.frameFeatures.splice(i, 1);
            }
        }
    }
    /**
     * Adds FrameFeature to FrameFeatureCollection and returns it
     *
     * @param feature will be added to this
     * @param needsUpdate true if framefeature has to be displayed on current frame;
     * set false if framefeature was created in a different frame then its supposed to be
     * @returns {FrameFeature} same framefeature but now with this as parentcollection
     */
    addFeature(feature, needsUpdate = true) {
        const newfeature = new FrameFeature(this);
        const a = JSON.parse(feature.toGeoJsonString());
        newfeature.parseFromJSON(a, needsUpdate);
        this.frameFeatures.push(newfeature);
        return newfeature;
    }
    /**
     * Adds FrameFeature from JSON Object to FrameFeatureCollection and returns it
     *
     * @param feature will be added to this
     * @param {boolean} needsUpdate
     * @returns {FrameFeature} same framefeature but now with this as parentcollection
     */
    addFeatureFromGeoJSON(feature, needsUpdate = true) {
        const newfeature = new FrameFeature(this);
        newfeature.parseFromJSON(feature, needsUpdate);
        this.frameFeatures.push(newfeature);
        return newfeature;
    }
    /**
     * Adds FrameFeature from JSON valid string to FrameFeatureCollection and returns it
     *
     * @param feature will be added to this
     * @returns {FrameFeature} same framefeature but now with this as parentcollection
     */
    addFeatureFromGeoJSONString(feature) {
        const a = JSON.parse(feature);
        return this.addFeatureFromGeoJSON(a);
    }
    update(currentFrameNum, currentSegmentId, currentStreamId, activeimage) {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].update(currentFrameNum, currentSegmentId, currentStreamId, activeimage);
        }
    }
    show() {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].show();
        }
        this.infrascene.render();
    }
    hide() {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].hide();
        }
        this.infrascene.render();
    }
    highlightOn() {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].highlightOn();
        }
        this.infrascene.render();
    }
    highlightOff() {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].highlightOff();
        }
        this.infrascene.render();
    }
    /**
     * finds framefeatures of this collection at mx,my position and adds it to pickedfeatures
     *
     * @param mx
     * @param my
     * @param pickedfeature
     */
    pick(mx, my, pickedfeature) {
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].pick(mx, my, pickedfeature);
        }
    }
    /**
     * Finds and returns FrameFeature in this with it's ObjectID
     *
     * @param id the ID of the Object in the DB of the FrameFeature set by the infra3dgeoserver
     * @param {number} fid
     * @returns {FrameFeature}
     */
    findSiblings(id, fid) {
        const siblingFeatures = [];
        for (const idx in this.frameFeatures) {
            if (this.frameFeatures[idx].properties.objectid == id && this.frameFeatures[idx].id != fid) {
                siblingFeatures.push(this.frameFeatures[idx]);
            }
        }
        return siblingFeatures;
    }
    /**
     * Returns the crs-Part (EPSG-Code) for a geojson-string.
     * It uses the epsg-code of the stream if its defined or a default epsg-code (21781, LV03).
     *
     * @return {string}
     */
    _getEPSGNodeString() {
        const geoframe = this.scene.getCurrentGeoframe();
        if (geoframe != null) {
            return ',"crs": {"type": "name","properties": {"name": "EPSG:' + geoframe.srid + '"}}';
        }
        else {
            return ',"crs": {"type": "name","properties": {"name": "EPSG:21781"}}'; //default value
        }
    }
    getFrameFeaturesByFrameStreamSegment(framenumber, streamid, segmentid) {
        const framefeatures = [];
        for (let i = 0; i < this.frameFeatures.length; i++) {
            const props = this.frameFeatures[i].properties;
            if (props.framenumber == framenumber && props.segmentid == segmentid && props.streamid == streamid) {
                framefeatures.push(this.frameFeatures[i]);
            }
        }
        return framefeatures;
    }
    changeStyle(style) {
        this.style = style || this.defaultstyle;
        for (let i = 0; i < this.frameFeatures.length; i++) {
            this.frameFeatures[i].updateStyle();
        }
    }
    setDefaultProps(properties) {
        this.defaultProperties = properties;
    }
    getDefaultProps() {
        if (this.defaultProperties)
            return JSON.parse(JSON.stringify(this.defaultProperties));
        return null;
    }
}
//# sourceMappingURL=framefeaturecollection.js.map